import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const isNurse = () => { try { return /nurse|\brn\b|lvn|lpn/i.test(JSON.parse(localStorage.getItem('ivory_user') || '{}').role || ''); } catch { return false; } };

const Stat = ({ label, value, id }) => (
  <div className="text-center"><p className="text-lg font-bold text-gray-900" data-testid={`bulk-${id}`}>{value || 0}</p><p className="text-[11px] text-gray-500">{label}</p></div>
);

export const BulkAutoFillCard = () => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const load = () => api.get('/bulk-prefill/latest').then(r => setJob(r.data.job)).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(timer.current); }, []);
  useEffect(() => {
    clearInterval(timer.current);
    if (job?.status === 'running') timer.current = setInterval(load, 4000);
  }, [job?.status]);

  if (isNurse()) return null;
  const running = job?.status === 'running';
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  const start = async () => {
    if (!window.confirm('Create pre-filled OASIS-E2 SOC and 485 drafts for every patient that does not have one yet, mark all patients Active, and create recert (Follow-up) OASIS + next 485 for patients within 5 days of their 60-day end date? Drafts need a nurse review and signature.')) return;
    try {
      const r = await api.post('/bulk-prefill/start');
      setJob(r.data.job);
      toast.success('Auto-fill started. You can keep working; this card shows progress.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start auto-fill');
    }
  };

  return (
    <div className="bg-white border border-[#d4af37]/40 rounded-lg p-4" data-testid="bulk-autofill-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#d4af37]" /> Auto-fill all charts</h3>
          <p className="text-sm text-gray-600">Creates OASIS-E2 SOC and 485 drafts for every patient, filled from the chart, uploaded files, prior assessments and medications, plus the recert OASIS and next 485 when the 60-day period ends within 5 days. Patients that already have them are skipped, so running it again is safe.</p>
        </div>
        <button onClick={start} disabled={running} className="px-4 py-2 rounded bg-gray-900 text-[#d4af37] font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="bulk-autofill-start">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {running ? 'Working...' : job ? 'Run again' : 'Start auto-fill'}
        </button>
      </div>
      {job && (
        <div className="mt-4 space-y-3" data-testid="bulk-autofill-progress">
          <div className="flex justify-between text-xs text-gray-600">
            <span data-testid="bulk-status">{running ? `Working on ${job.done} of ${job.total} patients` : job.status === 'completed' ? `Finished ${new Date(job.finishedAt).toLocaleString()}` : `Stopped (${job.status}). Press Run again to continue where it left off.`}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded"><div className="h-2 bg-[#d4af37] rounded transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <Stat id="soc" label="OASIS SOC drafts" value={job.socCreated} />
            <Stat id="p485" label="485 drafts" value={job.p485Created} />
            <Stat id="recert" label="Recerts created" value={job.recertCreated} />
            <Stat id="skipped" label="Already had both" value={job.skipped} />
            <Stat id="activated" label="Set to Active" value={job.activated} />
            <Stat id="errors" label="Errors" value={job.errors} />
          </div>
          {job.errorPatients?.length > 0 && <p className="text-xs text-red-700" data-testid="bulk-error-list">Could not fill: {job.errorPatients.join(', ')}</p>}
        </div>
      )}
    </div>
  );
};
